import { HTMLChakraProps, mergeRefs, ScrollArea as ChakraScrollArea, useSlotRecipe } from '@chakra-ui/react';
import { ComponentProps, forwardRef, ReactNode, RefObject, useLayoutEffect, useRef, useState } from 'react';
import { useStickToBottom } from 'use-stick-to-bottom';

type ScrollAreaProps = {
  children?: ReactNode;
  variant?: 'border-900';
  orientation?: 'horizontal' | 'vertical';
  size?: 'small' | 'medium';
  backgroundColor?: string;
  stickToBottom?: boolean;
  viewportRef?: RefObject<HTMLDivElement | null>;
  contentProps?: ComponentProps<typeof ChakraScrollArea.Content>;
};

export const ScrollArea = forwardRef<HTMLDivElement, ScrollAreaProps & HTMLChakraProps<'div'>>(
  (
    {
      children,
      variant = 'border-900',
      orientation = 'vertical',
      size = 'medium',
      stickToBottom = false,
      viewportRef,
      contentProps,
      ...rest
    },
    ref,
  ) => {
    const sticky = useStickToBottom();
    const recipe = useSlotRecipe({ key: 'scrollArea' });
    const [variantProps] = recipe.splitVariantProps({ variant });
    const { root, viewport, content, scrollbar, thumb } = recipe({ ...variantProps, size });

    const localViewportRef = useRef<HTMLDivElement | null>(null);
    const localContentRef = useRef<HTMLDivElement | null>(null);

    const [hasOverflow, setHasOverflow] = useState(false);

    useLayoutEffect(() => {
      const viewportElement = localViewportRef.current;
      const contentElement = localContentRef.current;
      if (!viewportElement || !contentElement) return;

      const measure = () => {
        const overflow =
          orientation === 'vertical'
            ? contentElement.scrollHeight > viewportElement.clientHeight
            : contentElement.scrollWidth > viewportElement.clientWidth;
        setHasOverflow(overflow);
      };
      measure();

      const observer = new ResizeObserver(measure);
      observer.observe(contentElement);
      return () => observer.disconnect();
    }, [orientation]);

    return (
      <ChakraScrollArea.Root ref={ref} size={size} {...rest} {...root} data-orientation={orientation}>
        <ChakraScrollArea.Viewport
          {...viewport}
          ref={mergeRefs(localViewportRef, viewportRef, stickToBottom ? sticky.scrollRef : undefined)}
        >
          <ChakraScrollArea.Content
            {...content}
            {...contentProps}
            ref={mergeRefs(localContentRef, stickToBottom ? sticky.contentRef : undefined)}
          >
            {children}
          </ChakraScrollArea.Content>
        </ChakraScrollArea.Viewport>
        <ChakraScrollArea.Scrollbar
          {...scrollbar}
          orientation={orientation}
          data-force-overflow={hasOverflow || undefined}
        >
          <ChakraScrollArea.Thumb {...thumb} />
        </ChakraScrollArea.Scrollbar>
      </ChakraScrollArea.Root>
    );
  },
);
